import { selectItemType } from '@src/types';
import { AsteriskOutlinedIcon } from '@src/svg';
import { ControlledSelectPropsType } from '@uikit/FormItems/constants';
import { getDynamicValidationColorsGlobal } from '@uikit/FormItems/ControlledItems/helpers';
import React, { FC, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import Select, { components } from 'react-select';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';
import { TooltipWrapper } from '@uikit/TooltipWrapper';
import { useTextTruncation } from '@hooks/useTextTruncation';

export const ControlledSelect: FC<ControlledSelectPropsType> = ({
  name,
  value,
  info,
  requestByAsync,
  title,
  required = false,
  IconLeft,
  IconRight,
  iconRightTooltip,
  selectOptions,
  changeSelect,
  isMulti,
  disabled,
  maxWidth,
  control,
  rules,
  styles = {},
  placeholder,
  paramByAsync,
  valueContainerStyles = {},
  menuPlacement = 'bottom',
  menuDisabled = false,
  getUpdatedOption,
  removeCaret = false,
  // not used
  trigger,
  isClearable = false,
  shouldDelete = false,
  noDataAvailable = 'No options',
  tabIndex,
  dataTestId,
  onIconClick,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const prevOptionsRef = useRef<Array<selectItemType>>([]);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const { tooltipContent, bindTruncation } = useTextTruncation(
    '.custom-select__single-value'
  );

  const handleChange = (selectedOption: any) => {
    if (isMulti) {
      changeSelect && changeSelect(selectedOption || [], { name });
      const previous = Array.isArray(prevOptionsRef.current)
        ? prevOptionsRef.current
        : [];

      const addedOption = (selectedOption || []).filter(
        (option: selectItemType) =>
          !previous?.some((prev) => prev.id === option.id)
      )[0];

      const removedOption = previous?.filter(
        (option) =>
          !(selectedOption || []).some(
            (current: selectItemType) => current.id === option.id
          )
      )[0];

      getUpdatedOption &&
        getUpdatedOption(addedOption || removedOption, { name });

      prevOptionsRef.current = selectedOption || [];
    } else {
      changeSelect && changeSelect(selectedOption, { name });
    }
  };

  const noOptionsMessage = loading ? (
    <div>Loading data...</div>
  ) : (
    <div>{noDataAvailable}</div>
  );

  const handleMenuClose = () => {
    setMenuIsOpen(false);
  };

  const handleMenuOpen = async () => {
    if (menuIsOpen) {
      setMenuIsOpen(false);
      return;
    }

    setLoading(true);
    setMenuIsOpen(true);
    try {
      requestByAsync && (await requestByAsync(paramByAsync));
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const customComponents = useMemo(() => {
    const baseComponents: any = {
      Control: (props: any) => (
        <components.Control
          {...props}
          innerProps={{ ...props.innerProps, 'data-test-id': dataTestId }}
        />
      ),
      Input: (props: any) => (
        <components.Input {...props} data-test-id={`${dataTestId}-input`} />
      ),
      Option: (props: any) => (
        <components.Option
          {...props}
          innerProps={{
            ...props.innerProps,
            'data-test-id': `${dataTestId}-option-${props.data.id}`,
          }}
        />
      ),
    };

    if (removeCaret || disabled) {
      baseComponents.DropdownIndicator = () => null;
      baseComponents.IndicatorSeparator = () => null;
    }

    return baseComponents;
  }, [removeCaret, disabled, dataTestId]);

  const normalizedOptions = useMemo(
    () =>
      Array.isArray(selectOptions)
        ? selectOptions.map((option) => ({
            ...option,
            value: option?.value ?? option?.id ?? option?.name,
            label: option?.label ?? option?.value,
            isDisabled: option?.isDisabled ?? option?.disabled,
          }))
        : [],
    [selectOptions]
  );

  return (
    <div
      style={{ maxWidth }}
      className="input-text"
      data-field-name={name}
      data-test-id={dataTestId}
    >
      {info?.toggle && (
        <InfoTooltip className="info_shower" title={info?.desc}>
          <div></div>
        </InfoTooltip>
      )}
      <div className={!disabled ? '' : 'disabled'}>
        <div className="input-title-wrapper">
          {title && (
            <span className="input-title">
              {title}
              {(required || !!rules?.required || !!rules?.validate) && (
                <span className="input-required-marker" aria-hidden="true">
                  <AsteriskOutlinedIcon
                    width={10}
                    height={10}
                    color="#fb6403"
                  />
                </span>
              )}
            </span>
          )}
        </div>
        <Controller
          key={title}
          name={name}
          control={control}
          rules={rules}
          render={({ field, fieldState }) => {
            const validationDynamicClassName = getDynamicValidationColorsGlobal(
              field,
              !!fieldState.error
            );

            prevOptionsRef.current = Array.isArray(field.value)
              ? field.value
              : [];

            const dynamicComponents =
              isClearable && field.value
                ? customComponents
                : {
                    ...customComponents,
                    IndicatorSeparator: () => null,
                  };

            return (
              <TooltipWrapper
                tooltip={tooltipContent}
                open={Boolean(tooltipContent)}
              >
                <div className="controlled__select" {...bindTruncation}>
                  {IconLeft && typeof IconLeft === 'function' && (
                    <div
                      className="select-icon"
                      style={{ pointerEvents: 'none' }}
                    >
                      <IconLeft />
                    </div>
                  )}
                  {IconRight && (
                    <div className="select-icon select-icon-right">
                      {iconRightTooltip ? (
                        <InfoTooltip
                          title={iconRightTooltip}
                          placement="top"
                          childrenContainerStyle={{ width: 'auto' }}
                        >
                          <span
                            className="select-icon-tooltip-target"
                            {...(onIconClick && {
                              role: 'button',
                              tabIndex: 0,
                              onClick: onIconClick,
                            })}
                          >
                            {typeof IconRight === 'function' ? (
                              <IconRight />
                            ) : (
                              IconRight
                            )}
                          </span>
                        </InfoTooltip>
                      ) : onIconClick ? (
                        <span
                          className="select-icon-clickable"
                          role="button"
                          tabIndex={0}
                          onClick={onIconClick}
                        >
                          {typeof IconRight === 'function' ? (
                            <IconRight />
                          ) : (
                            IconRight
                          )}
                        </span>
                      ) : typeof IconRight === 'function' ? (
                        <IconRight />
                      ) : (
                        IconRight
                      )}
                    </div>
                  )}
                  <Select
                    {...field}
                    value={value ?? field.value}
                    menuPlacement={menuPlacement}
                    menuIsOpen={!menuDisabled && menuIsOpen}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Escape') {
                        setMenuIsOpen(false);
                      }
                    }}
                    onInputChange={() => {
                      trigger && trigger(name);
                    }}
                    onMenuOpen={handleMenuOpen}
                    onMenuClose={handleMenuClose}
                    classNamePrefix="custom-select"
                    classNames={{
                      control: () => validationDynamicClassName,
                    }}
                    options={normalizedOptions}
                    placeholder={placeholder}
                    isClearable={isClearable}
                    aria-required={required || undefined}
                    components={dynamicComponents}
                    getOptionValue={(option) =>
                      String(option?.value ?? option?.id ?? '')
                    }
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption);
                      handleChange(selectedOption);
                      setMenuIsOpen(false);
                      if (trigger) {
                        queueMicrotask(() => {
                          void trigger(name);
                        });
                      }
                    }}
                    onBlur={() => {
                      field.onBlur();
                      setMenuIsOpen(false);
                      trigger && trigger(name);
                    }}
                    styles={{
                      placeholder: (provided) => ({
                        ...provided,
                        fontSize: '16px',
                        display: removeCaret ? 'none' : 'initial',
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        padding: IconLeft ? '0 12px 0 28px' : '0 12px',
                        paddingRight: IconRight ? '32px' : '12px',
                        cursor: 'pointer',
                        ...styles,
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        ...valueContainerStyles,
                      }),
                      indicatorsContainer: (provided) => ({
                        ...provided,
                        alignItems: 'flex-start',
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isDisabled
                          ? '#2D0A76'
                          : state.isFocused
                            ? '#2D0A76'
                            : 'white',
                        color: state.isDisabled
                          ? '#fff'
                          : state.isFocused
                            ? '#fff'
                            : '#000',
                        fontSize: '12px',
                        '&:hover': {
                          backgroundColor: '#2D0A76',
                          color: '#fff',
                        },
                      }),
                      multiValueRemove: (base) => {
                        return shouldDelete
                          ? { ...base, display: 'none' }
                          : base;
                      },
                    }}
                    {...(tabIndex ? { tabIndex } : {})}
                    noOptionsMessage={() => noOptionsMessage}
                    isMulti={isMulti}
                    isDisabled={disabled}
                  />
                </div>
              </TooltipWrapper>
            );
          }}
        />
      </div>
    </div>
  );
};
